// Default in-code configuration for live broadcast availability.
// This is used when no environment override is provided or when the override is invalid.
const DEFAULT_LIVE_BROADCAST_ALLOWED: Record<string, string[]> = {
  us: ['en'],
  kr: ['ko'],
  se: ['sv'],
  es: ['es'],
  jp: ['ja', 'en'],
  no: ['no'],
  de: ['de'],
  fr: ['fr'],
  dk: ['da'],
  fi: ['fi'],
  rs: ['sr'],
};
/**
 * Loads the live broadcast allowlist from the environment variable
 * LIVE_BROADCAST_ALLOWED if present, falling back to the default configuration.
 *
 * The environment variable must be a JSON object mapping ISO country codes
 * to arrays of language codes, for example:
 *
 *   {"us":["en"],"jp":["ja","en"]}
 *
 * Country and language codes are normalized to lowercase.
 */
function loadLiveBroadcastAllowlistFromEnv(): Record<string, string[]> {
  const raw = process.env.LIVE_BROADCAST_ALLOWED;
  if (!raw) {
    return DEFAULT_LIVE_BROADCAST_ALLOWED;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return DEFAULT_LIVE_BROADCAST_ALLOWED;
    }
    const normalized: Record<string, string[]> = {};
    for (const [country, languages] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(languages)) {
        continue;
      }
      const normalizedLanguages = languages
        .map((lang) => String(lang).toLowerCase())
        .filter((lang) => lang.length > 0);
      if (normalizedLanguages.length === 0) {
        continue;
      }
      normalized[country.toLowerCase()] = normalizedLanguages;
    }
    // If parsing produced no valid entries, fall back to the default configuration.
    if (Object.keys(normalized).length === 0) {
      return DEFAULT_LIVE_BROADCAST_ALLOWED;
    }
    return normalized;
  } catch {
    // On any parse error, fall back to the default configuration to avoid runtime failures.
    return DEFAULT_LIVE_BROADCAST_ALLOWED;
  }
}
// Effective live broadcast allowlist. This can be overridden at runtime
// via the LIVE_BROADCAST_ALLOWED environment variable without redeploying code.
export const LIVE_BROADCAST_ALLOWED: Record<string, string[]> =
  loadLiveBroadcastAllowlistFromEnv();